import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import {
  FDContentContainer,
  FDStack,
} from '@facet-wealth/design';
import PageHeader from 'Components/ComponentLibrary/Molecules/PageHeader';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import useCollectionState from 'Hooks/useCollectionState';
import DataContainer from 'Components/UI/DataContainer';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import { Skeleton } from '@material-ui/lab';
import { CompleteActionItemBanner } from 'Components/ComponentLibrary/Organisms/CompleteActionItemBanner';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { dataAttrs } from 'Helpers/Testing';
import { PlanPageDisclosures } from './disclosures';
import { TotalValueProduced } from './totalValueProduced';
import { PlanSummaryPhases } from './planSummaryPhases';
import { StrongAlert } from './facetDesignLike/strongAlert';

const overrides = buildFacetDesignOverride()
  .nameOverride('alertMessage', {
    _component: () => StrongAlert,
    _customProps: { status: 'error' },
  }, overrideReadOnly)
  .nameOverride('disclosures', {
    _component: () => PlanPageDisclosures,
  }, overrideReadOnly);

export const PlanSummaryPageTemplate = ({ resource }) => {
  useBoneBackgroundColor();
  // I'm explicitly using relationship instead of withRelationship here because this entire page will not function and should blow up if this relationship is missing. You should never have the planSummaryPage resource retrievable but the planSummary resource not retrievable.
  const planSummaryLink = resource.relationship(fr.relationships.planSummary.name).link('related').url();
  const {
    collection,
    isInitializing,
  } = useCollectionState({ link: planSummaryLink });
  const planSummary = useMemo(() => collection?.resources()?.head(), [collection]);

  const { loadPageContentLink } = usePageContentContext();

  React.useEffect(() => {
    planSummary?.withRelationship(fr.relationships.selectScenario.name, (rel) => {
      loadPageContentLink(rel.link('related'));
    }, () => null);
  }, [planSummary]);

  return (
    <>
      <CompleteActionItemBanner resource={resource} btnText="Let's go" />
      <FDContentContainer maxWidth={'lg'} {...dataAttrs('plan-page', resource)}>
        <FDStack direction="column" spacing={2}>
          <PageHeader
            title={resource.attr('title')}
            subtitle={<MetaControl resource={resource} attrName="description" metaOverrideSet={overrides} />}
            miniHero={planSummary
              ? (
                <TotalValueProduced
                  prompt={planSummary.meta().field('totalValueProduced').attr('prompt')}
                  description={planSummary.meta().field('totalValueProduced').attr('description')}
                  value={planSummary.attr('totalValueProduced')}
                />
              )
              : (
                <Skeleton animation="wave">
                  <TotalValueProduced
                    prompt="This is for the skeleton to be the right size"
                    value="$$$$$ in total value*"
                    description="*Total value by following your Facet Foundations plan, in the form of interest saved and employer contributions."
                  />
                </Skeleton>
              )}
          />
          {planSummary && <MetaControl resource={planSummary} attrName="alertMessage" metaOverrideSet={overrides} />}
          <DataContainer
            loading={isInitializing}
            component={PlanSummaryPhases}
            componentProps={{
              resource: planSummary,
            }}
          />
          {planSummary && <MetaControl resource={planSummary} attrName="disclosures" metaOverrideSet={overrides} />}
        </FDStack>
      </FDContentContainer>
    </>
  );
};

PlanSummaryPageTemplate.defaultProps = {
};

PlanSummaryPageTemplate.propTypes = {
  resource: PropTypes.object.isRequired,
};
